import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import MaskReveal from '@/components/motion/mask-reveal'
import Magnetic from '@/components/motion/magnetic'
import LookbookIntro from '@/components/lookbook/intro'
import LooksGrid from '@/components/lookbook/looks-grid'
import Credits from '@/components/lookbook/credits'
import { lookbookChapters } from '@/constants/content'
import { media } from '@/constants/media'

const Lookbook: FC = (): ReactNode => {
    const [concrete, nightShift, softArmor] = lookbookChapters

    return (
        <Layout>
            <Seo
                title='Lookbook'
                description="SS26 Essentials 04 in three chapters: Concrete, shot at dawn on Tempelhof; Night Shift, 2 to 5 a.m. in Berlin-Mitte; and Soft Armor, heavy knits and quiet shapes."
                ogImage={media.concreteArcCoat}
            />

            <LookbookIntro />

            <MaskReveal
                id={concrete.id}
                words={concrete.words}
                caption={concrete.caption}
                label={`Chapter ${concrete.numeral}`}
                image={concrete.image}
                alt={concrete.alt}
                shape={concrete.shape}
                focus={concrete.focus}
            />

            <LooksGrid />

            <MaskReveal
                id={nightShift.id}
                words={nightShift.words}
                caption={nightShift.caption}
                label={`Chapter ${nightShift.numeral}`}
                image={nightShift.image}
                alt={nightShift.alt}
                shape={nightShift.shape}
                focus={nightShift.focus}
            />

            <MaskReveal
                id={softArmor.id}
                words={softArmor.words}
                caption={softArmor.caption}
                label={`Chapter ${softArmor.numeral}`}
                image={softArmor.image}
                alt={softArmor.alt}
                shape={softArmor.shape}
                focus={softArmor.focus}
            />

            <Credits />

            <section aria-label='Shop the looks' className='flex flex-col items-center border-t border-line py-28 text-center gutter md:py-40'>
                <p data-reveal='fade' className='label-mono text-acid'>
                    (Seen something?)
                </p>

                <Magnetic strength={0.25} className='mt-8'>
                    <Link
                        href='/shop'
                        data-cursor='Shop'
                        className='group inline-flex items-center gap-[0.2em] font-heading text-[clamp(44px,7vw,120px)] leading-[0.9] text-bone transition-colors duration-500 hover:text-acid'
                    >
                        Shop the looks

                        <ArrowRight aria-hidden='true' strokeWidth={1.5} className='size-[0.75em] transition-transform duration-700 ease-expo-out group-hover:translate-x-[0.12em]' />
                    </Link>
                </Magnetic>
            </section>
        </Layout>
    )
}

export default Lookbook
